import {
  BulkDeleteWithConfirmButton,
  Datagrid,
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  List,
  Pagination,
  TextField,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  TextField as MuiTextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Autocomplete,
  DialogActions,
} from "@mui/material";
import "./styles.css";
import { useEffect, useState } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { apiSettingsApi } from "../../providers/env";
import {
  Params,
  Link as RouterLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

const VariablePagination = (props: any) => (
  <Pagination rowsPerPageOptions={[25, 50, 100]} {...props} />
);

export const ApiList = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  return (
    <List
      {...props}
      filters={[]}
      component="div"
      perPage={25}
      pagination={<VariablePagination />}
    >
      <ApiListContent agentId={agentId} />
    </List>
  );
};
const SearchBar = ({
  defaultKeyword,
  onSearch,
}: {
  defaultKeyword: string;
  onSearch: (kw: string) => void;
}) => {
  const [keyword, setKeyword] = useState(defaultKeyword);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <MuiTextField
        size="small"
        placeholder="Search keyword"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />
      <Button variant="contained" onClick={() => onSearch(keyword)}>
        Search
      </Button>
    </Box>
  );
};
const ApiListContent = ({ agentId }: any) => {
  const { filterValues = {}, setFilters } = useListContext();
  const [openModal, setOpenModal] = useState(false);
  const [keywordApi, setKeywordApi] = useState("");
  const [id, setId] = useState("");
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const isOpenModal = (values: any) => {
    setKeywordApi(values?.keyword ?? "");
    setOpenModal(!openModal);
    setId(values?.id ?? "");
  };

  const handleSubmit = async () => {
    try {
      if (id) {
        await apiSettingsApi.updateKeyword({
          vendorApiId: parseInt(id),
          keywordUpdate: { keyword: keywordApi.trim() } as any,
        });
      }
      refresh();
      notify("Update successfully", { type: "success" });
    } catch (error: any) {
      notify(error, { type: "error" });
    }
  };
  const handleSearch = (kw: string) => {
    if (typeof setFilters === "function") {
      setFilters({ ...filterValues, keyword: kw }, null);
    }
  };

  const ActionsFunctionField = ({ agentId }: { agentId?: string }) => (
    <FunctionField
      label="Actions"
      render={(record: any) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <IconButton
            component={RouterLink}
            to={`/vendor-api-master/${agentId}/${record.id}`}
            size="small"
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteWithConfirmButton
            label=""
            sx={{
              minWidth: 0,
              borderRadius: "50%",
              p: "7px",
              color: "error.main",
              "& .MuiButton-startIcon": {
                margin: 0,
              },
            }}
            record={record}
            confirmTitle="Are you sure?"
            confirmContent="You won't be able to revert this!"
            mutationMode="pessimistic"
            redirect={`/vendor-api-master/${agentId}`}
          />
          <IconButton
            size="small"
            onClick={() => isOpenModal(record)}
            aria-label="settings"
          >
            <SettingsIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={() =>
              navigate(
                `/vendor-api-master/${agentId}/duplicate-api/${record.id}`
              )
            }
            aria-label="copy"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    />
  );

  const KeywordSelector = ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (val: string) => void;
  }) => {
    const initOptions = () => {
      if (typeof value === "string") {
        return value.split(",").filter((v) => v.trim() !== "");
      }
      return [];
    };

    const [options, setOptions] = useState<string[]>(initOptions);

    const parsedValue = value
      ? value.split(",").filter((v) => v.trim() !== "")
      : [];

    useEffect(() => {
      // Ensure that options include parsedValue
      setOptions((prev) => Array.from(new Set([...prev, ...parsedValue])));
    }, [value]);

    const handleChange = (_: any, newValue: string[]) => {
      const cleaned = newValue.map((v) => v.trim()).filter((v) => v !== "");

      const merged = Array.from(new Set([...options, ...cleaned]));
      setOptions(merged);
      onChange(cleaned.join(","));
    };

    return (
      <Autocomplete
        multiple
        freeSolo
        filterSelectedOptions
        options={options}
        value={parsedValue}
        onChange={handleChange}
        renderInput={(params) => (
          <MuiTextField {...params} label="" fullWidth />
        )}
        style={{ marginBottom: 8, width: "100%" }}
      />
    );
  };

  return (
    <>
      <Card sx={{ m: 2 }}>
        <CardHeader title="Manage Apis" />
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
            <SearchBar
              defaultKeyword={filterValues.keyword || ""}
              onSearch={handleSearch}
            />
          </Box>
          <Datagrid
            bulkActionButtons={
              <>
                <BulkDeleteWithConfirmButton
                  confirmTitle="Are you sure?"
                  confirmContent="This will permanently delete the selected variables."
                  mutationMode="pessimistic"
                />
              </>
            }
          >
            <TextField source="api" cellClassName="column-api" />

            <TextField
              source="description"
              cellClassName="column-description"
            />
            <TextField source="keyword" cellClassName="column-keyword" />
            <FunctionField
              label="Actions"
              render={(record: any) => (
                <ActionsFunctionField agentId={agentId} />
              )}
              cellClassName="column-actions"
            />
          </Datagrid>
        </CardContent>
      </Card>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 20 }}>Keywords</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              padding: "8px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              marginBottom: "14px",
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{
                whiteSpace: "nowrap",
                marginBottom: 0,
              }}
            >
              Tags should be comma-separated. Example:{" "}
              <strong>tag1, tag2, tag3</strong>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography
              variant="body1"
              gutterBottom
              sx={{
                whiteSpace: "nowrap",
                marginBottom: 0,
                minWidth: 120,
              }}
            >
              Keyword
            </Typography>
            <KeywordSelector value={keywordApi} onChange={setKeywordApi} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setOpenModal(false);
              handleSubmit();
            }}
            variant="contained"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
